import { useEffect, useRef, useState } from 'react';
import type { Connector } from '@wagmi/core';
import { chain } from '../../config/chain';
import { DEV_WALLET_ENABLED } from '../../config/env';
import { useEthBalance } from '../../hooks/launch';
import { useBlockNumber } from '../../hooks/chainClock';
import { formatEth, shortAddress } from '../../lib/format';
import { explainError } from '../../tx/errors';
import { devAccounts, DEV_CONNECTOR_TYPE } from '../../wallet/devAccounts';
import { connectWallet, disconnectWallet, switchToAppChain, useConnectors, useWallet } from '../../wallet/hooks';
import { Dialog } from '../Dialog';
import { PixelIcon } from '../pixel/icons';

function browserWallets(connectors: readonly Connector[]): Connector[] {
  const injected = connectors.filter((c) => c.type === 'injected');
  const discovered = injected.filter((c) => c.id !== 'injected');
  if (discovered.length) return discovered;
  return typeof window !== 'undefined' && (window as { ethereum?: unknown }).ethereum ? injected : [];
}

export function WalletButton() {
  const wallet = useWallet();
  const [open, setOpen] = useState(false);
  // HASHCURVE runs only on Robinhood Chain: a wallet on any other network is asked to switch (or add the
  // network) once per connected address; the red button stays as the manual fallback.
  const asked = useRef<string | null>(null);
  useEffect(() => {
    if (wallet.status !== 'connected' || !wallet.wrongChain || !wallet.address) return;
    if (asked.current === wallet.address) return;
    asked.current = wallet.address;
    void switchToAppChain().catch(() => undefined);
  }, [wallet.status, wallet.wrongChain, wallet.address]);

  if (wallet.status === 'connected' && wallet.address) {
    return (
      <div className="row" style={{ gap: 8 }}>
        {wallet.wrongChain ? (
          <button type="button" className="btn btn-danger btn-sm" onClick={() => void switchToAppChain().catch(() => undefined)} data-testid="switch-chain">
            Wrong network — switch to {chain.name}
          </button>
        ) : null}
        <button type="button" className="btn btn-ghost" onClick={() => setOpen(true)} data-testid="wallet-address" title={wallet.address}>
          <PixelIcon name="wallet" px={1} />
          {wallet.isLocalTestAccount ? <span style={{ color: 'var(--molten)' }}>Test</span> : null}
          <span className="mono" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 15 }}>{shortAddress(wallet.address)}</span>
        </button>
        <AccountDialog open={open} onClose={() => setOpen(false)} />
      </div>
    );
  }
  return (
    <>
      <button type="button" className="btn btn-primary" onClick={() => setOpen(true)} data-testid="connect-wallet" disabled={wallet.status === 'connecting' || wallet.status === 'reconnecting'}>
        <PixelIcon name="wallet" px={1} />
        {wallet.status === 'connecting' || wallet.status === 'reconnecting' ? 'Connecting…' : 'Connect wallet'}
      </button>
      <ConnectDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function ConnectDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const connectors = useConnectors();
  const [devIndex, setDevIndex] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const wallets = browserWallets(connectors);

  const go = async (c: Connector | undefined) => {
    if (!c) return;
    setError(null);
    setPending(true);
    try {
      await connectWallet(c);
      onClose();
    } catch (e) {
      setError(explainError(e));
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Connect a wallet" testId="connect-dialog">
      <section className="stack" style={{ gap: 8 }}>
        <h3 className="plate-title" style={{ margin: 0 }}>
          Browser wallet
        </h3>
        <p className="small muted" style={{ margin: 0 }} data-testid="connect-chain-note">
          HASHCURVE runs only on {chain.name} (chain id {chain.id}), with any EVM wallet. After you connect, your wallet asks to switch to {chain.name}, or to add
          it if it is not set up yet.
        </p>
        {wallets.length ? (
          wallets.map((c) => (
            <button key={c.uid} type="button" className="btn btn-ghost btn-block" style={{ justifyContent: 'flex-start' }} onClick={() => void go(c)} disabled={pending} data-testid={`connector-${c.id}`}>
              {c.icon ? <img src={c.icon} alt="" width={20} height={20} /> : null}
              {c.id === 'injected' ? 'Browser wallet' : c.name}
            </button>
          ))
        ) : (
          <p className="small muted">No browser wallet detected. MetaMask, Rabby and Coinbase Wallet extensions work here.</p>
        )}
      </section>
      {DEV_WALLET_ENABLED ? (
        <section className="stack" style={{ gap: 8 }}>
          <hr className="rule" style={{ margin: '4px 0' }} />
          <h3 className="plate-title" style={{ margin: 0 }}>
            Local test account
          </h3>
          <p className="small muted" style={{ margin: 0 }}>
            Anvil development keys for the local chain only. They hold test ETH with no value, and this page signs for them without a wallet prompt.
          </p>
          <div className="row" style={{ flexWrap: 'nowrap' }}>
            <label className="sr-only" htmlFor="dev-account">
              Local test account
            </label>
            <select id="dev-account" className="select mono" value={devIndex} onChange={(e) => setDevIndex(Number(e.target.value))} data-testid="dev-account-select">
              {devAccounts.map((a) => (
                <option key={a.index} value={a.index}>
                  #{a.index} {shortAddress(a.address)}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-primary"
              disabled={pending}
              data-testid="dev-account-connect"
              onClick={() => void go(connectors.find((c) => c.type === DEV_CONNECTOR_TYPE && c.id === `local-test-account-${devIndex}`))}
            >
              Use test account
            </button>
          </div>
        </section>
      ) : null}
      {error ? (
        <div className="notice" data-tone="error">
          {error}
        </div>
      ) : null}
    </Dialog>
  );
}

function AccountDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const wallet = useWallet();
  const block = useBlockNumber();
  const balance = useEthBalance(open ? wallet.address : undefined, block);
  return (
    <Dialog open={open} onClose={onClose} title={wallet.isLocalTestAccount ? 'Local test account' : 'Wallet'} testId="account-dialog">
      <dl className="kv">
        <dt>Address</dt>
        <dd className="mono small" style={{ overflowWrap: 'anywhere' }}>
          {wallet.address}
        </dd>
        <dt>Connected with</dt>
        <dd>{wallet.connector?.name ?? '—'}</dd>
        <dt>Balance</dt>
        <dd>{balance.data !== undefined ? formatEth(balance.data) : '—'}</dd>
        <dt>Network</dt>
        <dd>{wallet.wrongChain ? <span className="warn">Not {chain.name}</span> : chain.name}</dd>
      </dl>
      <button
        type="button"
        className="btn btn-danger"
        data-testid="disconnect-wallet"
        onClick={() => {
          void disconnectWallet();
          onClose();
        }}
      >
        Disconnect
      </button>
    </Dialog>
  );
}
